import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Trophy, Users, Award, Calendar, Zap } from 'lucide-react';

const TournamentComingSoon = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg mr-4"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Back to Home
          </h1>
        </div>

        {/* Coming Soon Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="max-w-2xl mx-auto">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="mx-auto w-24 h-24 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-8"
            >
              <Trophy className="w-12 h-12 text-white" />
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl font-bold text-gray-900 dark:text-white mb-4"
            >
              Tournaments Coming Soon!
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xl text-gray-600 dark:text-gray-300 mb-8"
            >
              Get ready to compete in epic debate tournaments with amazing prizes
            </motion.p>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700 mb-8"
            >
              <div className="flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-yellow-500 mr-2" />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  Tournament Features Coming
                </span>
              </div>
              
              <div className="space-y-6 text-left">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-semibold">Championship Tournaments</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Compete in seasonal championships with substantial prize pools and exclusive rewards
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-semibold">Multiple Formats</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Lincoln-Douglas, Parliamentary, IPDA, and more debate formats available
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-semibold">Bracket System</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Single elimination, double elimination, and Swiss system tournaments
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-semibold">Live Streaming</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Watch top debates live and cheer for your favorite competitors
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-semibold">Ranking System</p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      Global rankings, seasonal leaderboards, and achievement badges
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Tournament Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg p-6 border border-yellow-200 dark:border-yellow-700 mb-8"
            >
              <div className="flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-yellow-600 mr-2" />
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  Upcoming Tournament Preview
                </span>
              </div>
              
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Summer Grand Prix 2024
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  The biggest tournament of the summer featuring multiple debate formats
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-yellow-600 mb-1">$2,000</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Prize Pool</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600 mb-1">256</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Participants</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600 mb-1">3</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Days</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600 mb-1">Mixed</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Format</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={() => navigate('/training')}
                className="btn-primary text-lg px-8 py-4 flex items-center justify-center"
              >
                <span>Practice with Training Modules</span>
              </button>
              
              <button
                onClick={() => navigate('/leaderboard')}
                className="btn-primary text-lg px-8 py-4 flex items-center justify-center"
              >
                <span>View Leaderboard</span>
              </button>
            </motion.div>

            {/* Progress Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="mt-12"
            >
              <div className="text-center mb-4">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tournament System Development
                </p>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 max-w-md mx-auto">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "60%" }}
                  transition={{ delay: 0.9, duration: 1.5, ease: "easeOut" }}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 h-3 rounded-full"
                ></motion.div>
              </div>
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-2">
                60% Complete
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TournamentComingSoon; 